import React from "react"
import { graphql } from "gatsby"
import styled from "styled-components"
import { Title } from "../atoms/Typography"
import Section from "../atoms/Section"
import Accordions from "../molecules/Accordions"
import Faq from "../molecules/Faq"
import SectionTitle from "../molecules/SectionTitle"

/** Scopes justify-content to MUI's AccordionSummary content row */
const StyledAccordionsHost = styled.div`
  & .MuiAccordionSummary-content,
  & .MuiAccordionSummary-content.MuiAccordionSummary-contentGutters {
    justify-content: ${props => props.$justifyContent || "flex-start"};
  }
`

const Layout = styled.div`
  display: flex;
  flex-direction: ${props => props.direction};
  gap: 2rem;
  align-items: ${props => props.alignItems};
  width: 100%;
`

const Half = styled.div`
  width: ${props =>
    props.customWidth
      ? `${props.customWidth}%`
      : props.isSideBySide
        ? "50%"
        : "100%"};
`

const Faqs = ({
  section,
  sectionTitle,
  faqs,
  card,
  labelStyles,
  contentStyles,
  alignText, // "left" | "center" | "right"
  listLocation = "bottom", // "top" | "bottom" | "left" | "right"
  listRatio = 50,
}) => {
  const textAlign = alignText || "left"

  const justifyMap = { left: "flex-start", center: "center", right: "flex-end" }
  const justifyContent = justifyMap[textAlign] || "flex-start"

  const caretGapPx = 6 // small gap so caret is not tight when right-aligned

  const faqItems =
    faqs?.map(({ title, richText, faqItemRest }) => ({
      label: (
        <Title
          textAlign={textAlign}
          variant="h3"
          mb={3}
          mr={textAlign === "right" ? `${caretGapPx}px` : 0} // add space from the caret on right align
          {...faqItemRest}
          {...labelStyles}
        >
          {title}
        </Title>
      ),
      content: (
        <Faq
          title={title}
          richText={richText}
          textAlign={textAlign}
          {...contentStyles}
        />
      ),
    })) || []

  const directionMap = {
    top: "column-reverse",
    bottom: "column",
    left: "row-reverse",
    right: "row",
  }

  const direction = directionMap[listLocation] || "column"
  const isSideBySide = listLocation === "left" || listLocation === "right"
  const ratio = Math.max(0, Math.min(100, parseInt(listRatio, 10)))
  const faqWidth = isSideBySide ? ratio : null
  const titleWidth = isSideBySide ? 100 - ratio : null

  return (
    <Section {...section}>
      <Layout
        direction={direction}
        alignItems={isSideBySide ? "flex-start" : "stretch"}
      >
        {sectionTitle && (
          <Half isSideBySide={isSideBySide} customWidth={titleWidth}>
            <SectionTitle {...sectionTitle} maxWidth={42} mb={6} />
          </Half>
        )}
        <Half isSideBySide={isSideBySide} customWidth={faqWidth}>
          <StyledAccordionsHost $justifyContent={justifyContent}>
            <Accordions
              items={faqItems}
              card={card}
              toggleMany={true}
              labelStyles={labelStyles}
              contentStyles={contentStyles}
            />
          </StyledAccordionsHost>
        </Half>
      </Layout>
    </Section>
  )
}

export default Faqs

export const query = graphql`
  fragment Faqs on STRAPI__COMPONENT_SECTIONS_FAQS {
    strapi_id
    FAQSSection: section {
      ...Section
    }
    sectionTitle {
      ...SectionTitle
    }
    faqs {
      ...Faq
    }
    card {
      ...Card
    }
    alignText
    listLocation
    listRatio
  }
`
